import { useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { Hash, Sparkles, Tag, X } from 'lucide-react';
import { useStore } from '../store';
import { normalizeNetName } from '../nets';

interface Proposal {
  edgeId: string;
  sheetId: string;
  sheetName: string;
  from: string;
  to: string;
}

/**
 * One place for all the "tidy up the naming/numbering" chores that used to be
 * scattered across the toolbar and the connection list:
 *   • Auto-number wires  — W1, W2… on any unnumbered wire (one click).
 *   • Normalize harness names — SUBSYSTEM-HARN-001 (one click).
 *   • Standardize net names — review-and-apply list (ground → RTN, PE, voltage
 *     format), preserving voltage/ISO prefixes so isolated returns stay distinct.
 * The first two report their result inline; the net-name pass keeps its
 * checklist so the user can uncheck individual renames before applying.
 */
export function StandardizeModal({ onClose }: { onClose: () => void }) {
  const sheets = useStore((s) => s.sheets);
  const updateEdgesAcrossSheets = useStore((s) => s.updateEdgesAcrossSheets);
  const autoNumberWires = useStore((s) => s.autoNumberWires);
  const normalizeHarnessNames = useStore((s) => s.normalizeHarnessNames);

  const [wireMsg, setWireMsg] = useState<string | null>(null);
  const [harnessMsg, setHarnessMsg] = useState<string | null>(null);

  const runAutoNumber = () => {
    const n = autoNumberWires();
    setWireMsg(n > 0 ? `${n} wire${n === 1 ? '' : 's'} numbered` : 'All wires already numbered');
  };
  const runNormalizeHarness = () => {
    const n = normalizeHarnessNames();
    setHarnessMsg(
      n > 0 ? `${n} harness${n === 1 ? '' : 'es'} renamed` : 'Harness names already normalized',
    );
  };

  const proposals = useMemo<Proposal[]>(() => {
    const out: Proposal[] = [];
    for (const sh of sheets) {
      if (sh.kind !== 'schematic') continue;
      for (const e of sh.edges) {
        const explicit = typeof e.data?.netName === 'string' ? e.data.netName.trim() : '';
        const label =
          typeof e.data?.label === 'string'
            ? e.data.label.trim()
            : typeof e.label === 'string'
              ? e.label.trim()
              : '';
        const from = explicit || label;
        if (!from) continue;
        const to = normalizeNetName(from);
        if (to && to !== from) {
          out.push({ edgeId: e.id, sheetId: sh.id, sheetName: sh.name, from, to });
        }
      }
    }
    return out.sort((a, b) => a.from.localeCompare(b.from) || a.to.localeCompare(b.to));
  }, [sheets]);

  // selected edgeIds — start with everything ticked
  const [skip, setSkip] = useState<Set<string>>(new Set());
  const toggle = (id: string) =>
    setSkip((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const selected = proposals.filter((p) => !skip.has(p.edgeId));

  const applyNets = () => {
    // group by target value so we issue one bulk update per distinct net name
    const byValue = new Map<string, string[]>();
    for (const p of selected) {
      (byValue.get(p.to) ?? byValue.set(p.to, []).get(p.to)!).push(p.edgeId);
    }
    for (const [value, ids] of byValue) updateEdgesAcrossSheets(ids, { netName: value });
    onClose();
  };

  return (
    <div style={overlayStyle} onMouseDown={onClose}>
      <div style={panelStyle} onMouseDown={(e) => e.stopPropagation()}>
        <div style={titleBarStyle}>
          <span style={titleStyle}>
            <Sparkles size={13} style={{ marginRight: 6, verticalAlign: '-2px', color: 'var(--accent)' }} />
            Standardize
          </span>
          <button style={closeBtnStyle} onClick={onClose} title="Close">
            <X size={14} />
          </button>
        </div>

        {/* ---- one-click tidy actions ---- */}
        <div style={actionsWrapStyle}>
          <ActionRow
            icon={<Hash size={15} />}
            title="Number wires"
            desc="Assign W1, W2… to every unnumbered wire."
            result={wireMsg}
            onRun={runAutoNumber}
          />
          <ActionRow
            icon={<Tag size={15} />}
            title="Normalize harness names"
            desc="Rename every harness as SUBSYSTEM-HARN-001."
            result={harnessMsg}
            onRun={runNormalizeHarness}
          />
        </div>

        {/* ---- net-name review + apply ---- */}
        <div style={sectionLabelStyle}>Net names</div>
        <div style={infoStyle}>
          Toprak/dönüş → <b>RTN</b>, koruyucu toprak → <b>PE</b>, voltaj formatı düzeltilir.
          Voltaj/ISO ön-ekleri korunur, böylece izole dönüşler ayrı net kalır.
        </div>

        <div style={listStyle}>
          {proposals.length === 0 ? (
            <div style={emptyStyle}>Tüm net adları zaten standart. Değiştirilecek bir şey yok.</div>
          ) : (
            proposals.map((p) => {
              const checked = !skip.has(p.edgeId);
              return (
                <label key={p.edgeId} style={rowStyle}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(p.edgeId)}
                    style={{ marginTop: 2, flexShrink: 0 }}
                  />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={nameRowStyle}>
                      <span style={fromStyle}>{p.from}</span>
                      <span style={arrowStyle}>→</span>
                      <span style={toStyle}>{p.to}</span>
                    </span>
                    <span style={metaStyle}>{p.sheetName}</span>
                  </span>
                </label>
              );
            })
          )}
        </div>

        <div style={footerStyle}>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            {selected.length} / {proposals.length} net seçili
          </span>
          <span style={{ flex: 1 }} />
          <button style={ghostBtnStyle} onClick={onClose}>
            Kapat
          </button>
          <button
            style={{ ...primaryBtnStyle, opacity: selected.length === 0 ? 0.5 : 1 }}
            disabled={selected.length === 0}
            onClick={applyNets}
          >
            <Sparkles size={13} style={{ marginRight: 5 }} />
            Net adlarını uygula ({selected.length})
          </button>
        </div>
      </div>
    </div>
  );
}

/** A one-click tidy action: icon + title/description on the left, a Run button
 *  on the right that swaps to an inline result once it has run. */
function ActionRow({
  icon,
  title,
  desc,
  result,
  onRun,
}: {
  icon: ReactNode;
  title: string;
  desc: string;
  result: string | null;
  onRun: () => void;
}) {
  return (
    <div style={actionRowStyle}>
      <span style={actionIconStyle}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={actionTitleStyle}>{title}</div>
        <div style={actionDescStyle}>{desc}</div>
      </div>
      {result && <span style={actionResultStyle}>{result}</span>}
      <button style={runBtnStyle} onClick={onRun}>
        Run
      </button>
    </div>
  );
}

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
};
const panelStyle: CSSProperties = {
  width: 560,
  maxWidth: '92vw',
  maxHeight: '80vh',
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--panel)',
  color: 'var(--text)',
  border: '1px solid var(--panel-border-strong)',
  borderTop: '2px solid var(--accent)',
  borderRadius: 'var(--radius)',
  boxShadow: 'var(--shadow-lg)',
  overflow: 'hidden',
};
const titleBarStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 10px 8px 14px',
  background: 'linear-gradient(var(--chrome-top), var(--chrome-bottom))',
  borderBottom: '1px solid var(--chrome-border)',
};
const titleStyle: CSSProperties = {
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: 0.8,
  textTransform: 'uppercase',
  display: 'inline-flex',
  alignItems: 'center',
};
const closeBtnStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 22,
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--panel)',
  color: 'var(--text-muted)',
  cursor: 'pointer',
  padding: 0,
};

const actionsWrapStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  padding: '10px 12px',
  borderBottom: '1px solid var(--panel-border)',
};
const actionRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '8px 10px',
  background: 'var(--panel-alt)',
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
};
const actionIconStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 26,
  height: 26,
  flexShrink: 0,
  borderRadius: 'var(--radius-sm)',
  background: 'var(--panel)',
  border: '1px solid var(--panel-border)',
  color: 'var(--accent)',
};
const actionTitleStyle: CSSProperties = { fontSize: 12.5, fontWeight: 700 };
const actionDescStyle: CSSProperties = { fontSize: 11, color: 'var(--text-muted)', marginTop: 1 };
const actionResultStyle: CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: 'var(--accent)',
  whiteSpace: 'nowrap',
};
const runBtnStyle: CSSProperties = {
  flexShrink: 0,
  border: '1px solid var(--panel-border-strong)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--panel)',
  color: 'var(--text)',
  padding: '5px 14px',
  fontSize: 11.5,
  fontWeight: 700,
  cursor: 'pointer',
};

const sectionLabelStyle: CSSProperties = {
  padding: '8px 14px 0',
  fontSize: 10,
  fontWeight: 800,
  letterSpacing: 0.8,
  textTransform: 'uppercase',
  color: 'var(--group-label)',
};
const infoStyle: CSSProperties = {
  padding: '4px 14px 8px',
  fontSize: 11,
  lineHeight: 1.5,
  color: 'var(--text-muted)',
};
const listStyle: CSSProperties = { overflowY: 'auto', padding: 6, minHeight: 100 };
const emptyStyle: CSSProperties = {
  padding: '28px 18px',
  textAlign: 'center',
  fontSize: 12,
  color: 'var(--text-muted)',
  lineHeight: 1.5,
};
const rowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 9,
  width: '100%',
  padding: '7px 9px',
  border: '1px solid transparent',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
};
const nameRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 12.5,
  fontFamily: 'ui-monospace, Consolas, monospace',
};
const fromStyle: CSSProperties = { color: 'var(--text-muted)', textDecoration: 'line-through' };
const arrowStyle: CSSProperties = { color: 'var(--text-muted)' };
const toStyle: CSSProperties = { fontWeight: 800, color: 'var(--accent)' };
const metaStyle: CSSProperties = {
  display: 'block',
  fontSize: 10.5,
  color: 'var(--text-muted)',
  marginTop: 2,
};
const footerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 12px',
  borderTop: '1px solid var(--panel-border)',
  background: 'var(--panel-alt)',
};
const ghostBtnStyle: CSSProperties = {
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--panel)',
  color: 'var(--text)',
  padding: '5px 11px',
  fontSize: 11.5,
  fontWeight: 700,
  cursor: 'pointer',
};
const primaryBtnStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  border: '1px solid var(--accent)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--accent-soft)',
  color: 'var(--accent)',
  padding: '5px 12px',
  fontSize: 11.5,
  fontWeight: 800,
  cursor: 'pointer',
};
